// theme-exempt-file: tailwind-merge's class groups, which name utilities as data, not classes on an element
// cn(): classes joined (clsx) and merged (tailwind-merge), a later class replacing an earlier one of its kind
import { type ClassValue, clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';
import { TOKENS, tokenUtility } from '@/modules/theme/tokens';

// The tokens' lureo- keys for a utility (rounded: lureo-radius-sm, lureo-radius, ...), from the token registry; a font
// utility is a family or a weight by its token's kind
const keysOf = (utility: string, kind?: string) =>
  TOKENS.flatMap(token => {
    const made = tokenUtility(token.name);
    return made?.utility === utility && token.kind !== 'gradient' && (!kind || token.kind === kind) ? [made.key] : [];
  });
const CORNERS = ['rounded', 'rounded-s', 'rounded-e', 'rounded-t', 'rounded-r', 'rounded-b', 'rounded-l', 'rounded-ss', 'rounded-se', 'rounded-ee', 'rounded-es', 'rounded-tl', 'rounded-tr', 'rounded-br', 'rounded-bl'];
const SIDES = ['', '-x', '-y', '-s', '-e', '-t', '-r', '-b', '-l'];
// The type scale's steps (text-lureo-13), each its size in pixels at the default root
const TYPE_SCALE = ['10', '11', '11-5', '12', '12-5', '13', '14', '14-4', '15', '16', '18', '20', '22', '24', '30', '36', '48', '60', 'lead', 'display'].map(step => `lureo-${step}`);

// Tailwind's merge, told about Lureo's lureo- classes so a later one replaces an earlier one of its kind, and a size or line
// width is never mistaken for a colour (text-lureo-13 beside text-lureo-ink, border-lureo-border-width beside border-lureo-line)
const twMerge = extendTailwindMerge<'lureo-font-width' | 'lureo-font-style' | 'lureo-decoration'>({
  extend: {
    classGroups: {
      shadow: [{ shadow: keysOf('shadow') }],
      ...Object.fromEntries(CORNERS.map(corner => [corner, [{ [corner]: keysOf('rounded') }]])),
      'font-family': [{ font: keysOf('font', 'font') }],
      'font-weight': [{ font: keysOf('font', 'weight') }],
      'text-transform': [{ case: keysOf('case') }],
      tracking: [{ tracking: keysOf('tracking') }],
      'font-size': [{ text: TYPE_SCALE }],
      opacity: [{ opacity: keysOf('opacity') }],
      ...Object.fromEntries(SIDES.map(side => [`border-w${side}`, [{ [`border${side}`]: keysOf('border') }]])),
      'divide-x': [{ 'divide-x': keysOf('border') }],
      'divide-y': [{ 'divide-y': keysOf('border') }],
      z: [{ z: keysOf('z') }],
      duration: [{ duration: keysOf('duration') }],
      ease: [{ ease: keysOf('ease') }],
      'backdrop-blur': [{ 'backdrop-blur': keysOf('backdrop-blur') }],
      'bg-image': [{ bg: TOKENS.filter(token => token.kind === 'gradient').map(token => tokenUtility(token.name)!.key) }],
      // Lureo's own font-*, decoration-* and other utilities, each its own kind, so none is taken for a family or a colour and dropped
      'lureo-font-width': [{ 'font-width': keysOf('font-width') }],
      'lureo-font-style': [{ 'font-style': keysOf('font-style') }],
      'lureo-decoration': [{ decoration: keysOf('decoration') }],
      indent: [{ indent: keysOf('indent') }],
      'text-shadow': [{ 'text-shadow': keysOf('text-shadow') }],
      scale: [{ scale: keysOf('scale') }],
      'translate-y': [{ 'translate-y': keysOf('translate-y') }],
      brightness: [{ brightness: keysOf('brightness') }],
    },
  },
})

export function cn(...inputs: ClassValue[]) {
  // eslint-disable-next-line tailwindcss/no-custom-classname -- the merge's own argument, not a class
  return twMerge(clsx(inputs))
}

